import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL || window.location.origin}/api`;

/**
 * Stripe integration card for the Settings → Integrations tab.
 * Lets a business owner paste their own Stripe Secret Key and configure
 * deposit %, enabled/disabled. Key is never exposed back to the client.
 */
export default function StripeIntegrationSection() {
  const [settings, setSettings] = useState({
    is_connected: false,
    key_preview: null,
    publishable_key: '',
    deposit_percent: 25,
    enabled: false,
    test_mode: true,
  });
  const [form, setForm] = useState({
    api_key: '',
    publishable_key: '',
    deposit_percent: 25,
    enabled: true,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [rotateMode, setRotateMode] = useState(false);
  const apiKeyInputRef = React.useRef(null);

  const startRotation = () => {
    setRotateMode(true);
    setTestResult(null);
    setForm((f) => ({ ...f, api_key: '' }));
    window.setTimeout(() => apiKeyInputRef.current?.focus(), 50);
  };

  const authHeader = () => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/integrations/stripe`, { headers: authHeader() });
      setSettings(data);
      setForm({
        api_key: '',
        publishable_key: data.publishable_key || '',
        deposit_percent: data.deposit_percent ?? 25,
        enabled: data.enabled ?? true,
      });
    } catch (e) {
      console.error('Load Stripe settings failed', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleSave = async () => {
    setSaving(true);
    setTestResult(null);
    try {
      const payload = {
        publishable_key: form.publishable_key || null,
        deposit_percent: parseFloat(form.deposit_percent) || 25,
        enabled: !!form.enabled,
      };
      if (form.api_key && form.api_key.trim()) {
        payload.api_key = form.api_key.trim();
      }
      await axios.post(`${API}/integrations/stripe`, payload, { headers: authHeader() });
      setForm({ ...form, api_key: '' }); // clear the secret input
      setRotateMode(false);
      await load();
      setTestResult({ ok: true, message: 'Saved successfully' });
    } catch (e) {
      setTestResult({ ok: false, message: e?.response?.data?.detail || 'Save failed' });
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await axios.post(`${API}/integrations/stripe/test`, {}, { headers: authHeader() });
      setTestResult({ ok: true, message: `Connected to Stripe account ${data.account_id || ''} ${data.country ? `(${data.country})` : ''}` });
    } catch (e) {
      setTestResult({ ok: false, message: e?.response?.data?.detail || 'Test failed' });
    } finally {
      setTesting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Disconnect Stripe? Existing payments will not be affected, but no new charges can be processed until you reconnect.')) return;
    try {
      await axios.post(`${API}/integrations/stripe/disconnect`, {}, { headers: authHeader() });
      await load();
      setTestResult({ ok: true, message: 'Stripe disconnected' });
    } catch (e) {
      setTestResult({ ok: false, message: e?.response?.data?.detail || 'Disconnect failed' });
    }
  };

  if (loading) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center text-sm text-gray-500">
        Loading Stripe settings…
      </div>
    );
  }

  return (
    <div
      className="bg-indigo-50 border border-indigo-200 rounded-lg p-6"
      data-testid="stripe-integration-section"
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex-1">
          <h4 className="font-semibold text-indigo-900 mb-2 flex items-center gap-2">
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
              <path d="M13.4 10.8c-1.7-.6-2.6-1-2.6-1.8 0-.7.6-1.1 1.7-1.1 1.4 0 3.2.6 4.4 1.3l.6-3.8c-1-.5-2.7-1-4.9-1-1.7 0-3.1.4-4.1 1.3-1 .9-1.6 2.1-1.6 3.6 0 2.6 1.6 3.7 4.3 4.7 1.7.6 2.3 1 2.3 1.8 0 .7-.6 1.2-1.8 1.2-1.6 0-4.1-.8-5.4-1.7l-.6 3.9c1.1.8 3.4 1.7 5.8 1.7 1.8 0 3.3-.4 4.4-1.2 1.2-.9 1.8-2.2 1.8-3.9 0-2.7-1.6-3.8-4.3-4.7l-.0-.3z"/>
            </svg>
            Stripe Payments
          </h4>
          <p className="text-indigo-700 text-sm">
            {settings.is_connected
              ? `Connected${settings.test_mode ? ' (Test Mode)' : ' (LIVE)'} • Key ${settings.key_preview}`
              : 'Accept card payments for appointment invoices and quote deposits.'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {settings.is_connected && (
            <span
              data-testid="stripe-encrypted-badge"
              title="API secret is encrypted at rest in the database"
              className="text-xs font-medium px-3 py-1 rounded-full bg-emerald-100 text-emerald-800 flex items-center gap-1"
            >
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              Encrypted at rest
            </span>
          )}
          {settings.is_connected && (
            <span className={`text-xs font-medium px-3 py-1 rounded-full ${settings.enabled ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}`}>
              {settings.enabled ? 'Active' : 'Disabled'}
            </span>
          )}
          {settings.test_mode && settings.is_connected && (
            <span className="text-xs font-medium px-3 py-1 rounded-full bg-blue-100 text-blue-800">Test Mode</span>
          )}
        </div>
      </div>

      <div className="space-y-4 bg-white rounded-lg p-4 border border-indigo-100">
        {rotateMode && (
          <div
            data-testid="stripe-rotate-banner"
            className="flex items-start gap-2 px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-amber-900 text-xs"
          >
            <svg className="w-4 h-4 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
            </svg>
            <div className="leading-relaxed">
              <p className="font-semibold mb-0.5">Rotating Stripe key</p>
              <p>
                Generate a new secret key in your{' '}
                <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noopener noreferrer" className="underline font-medium">Stripe dashboard</a>,
                paste it below, then click Save. The old key will be replaced and remain unrecoverable.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setRotateMode(false)}
              className="ml-auto text-amber-700 hover:text-amber-900 text-xs"
              data-testid="stripe-rotate-cancel-btn"
            >
              Cancel
            </button>
          </div>
        )}

        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-1">
            Secret Key {settings.is_connected && <span className="text-gray-400 font-normal">(leave blank to keep existing)</span>}
          </label>
          <input
            ref={apiKeyInputRef}
            type="password"
            value={form.api_key}
            onChange={(e) => setForm({ ...form, api_key: e.target.value })}
            placeholder={settings.is_connected ? settings.key_preview : 'sk_test_... or sk_live_...'}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 text-sm font-mono"
            data-testid="stripe-secret-key-input"
            autoComplete="new-password"
          />
          <p className="text-xs text-gray-500 mt-1">
            Get yours at{' '}
            <a
              href="https://dashboard.stripe.com/apikeys"
              target="_blank"
              rel="noopener noreferrer"
              className="text-indigo-600 hover:underline"
            >
              dashboard.stripe.com/apikeys
            </a>
            . Use Test Mode keys (sk_test_…) until you go live.
          </p>
        </div>

        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-1">
            Publishable Key <span className="text-gray-400 font-normal">(optional)</span>
          </label>
          <input
            type="text"
            value={form.publishable_key}
            onChange={(e) => setForm({ ...form, publishable_key: e.target.value })}
            placeholder="pk_test_... or pk_live_..."
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 text-sm font-mono"
            data-testid="stripe-publishable-key-input"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">Default Deposit %</label>
            <div className="relative">
              <input
                type="number"
                min={0}
                max={100}
                step={1}
                value={form.deposit_percent}
                onChange={(e) => setForm({ ...form, deposit_percent: e.target.value })}
                className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 text-sm"
                data-testid="stripe-deposit-percent-input"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">%</span>
            </div>
            <p className="text-xs text-gray-500 mt-1">Applied to quote deposit checkouts.</p>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">Enable Payments</label>
            <button
              type="button"
              onClick={() => setForm({ ...form, enabled: !form.enabled })}
              className={`w-full px-3 py-2 rounded-lg text-sm font-medium transition-colors ${form.enabled ? 'bg-green-100 text-green-700 border border-green-300' : 'bg-gray-100 text-gray-600 border border-gray-300'}`}
              data-testid="stripe-enabled-toggle"
            >
              {form.enabled ? '✓ Enabled' : 'Disabled'}
            </button>
            <p className="text-xs text-gray-500 mt-1">Toggle to pause checkouts.</p>
          </div>
        </div>

        {testResult && (
          <div className={`text-sm px-3 py-2 rounded-lg ${testResult.ok ? 'bg-green-50 border border-green-200 text-green-800' : 'bg-red-50 border border-red-200 text-red-800'}`}>
            {testResult.message}
          </div>
        )}

        <div className="flex gap-2 pt-2">
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:bg-gray-300 text-sm font-medium"
            data-testid="stripe-save-btn"
          >
            {saving ? 'Saving…' : 'Save Settings'}
          </button>
          {settings.is_connected && (
            <>
              <button
                type="button"
                onClick={handleTest}
                disabled={testing}
                className="px-4 py-2 bg-white text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 text-sm font-medium"
                data-testid="stripe-test-btn"
              >
                {testing ? 'Testing…' : 'Test Connection'}
              </button>
              {!rotateMode && (
                <button
                  type="button"
                  onClick={startRotation}
                  className="px-4 py-2 bg-amber-50 text-amber-800 border border-amber-300 rounded-lg hover:bg-amber-100 text-sm font-medium"
                  data-testid="stripe-rotate-key-btn"
                  title="Replace your existing Stripe secret key with a fresh one"
                >
                  Rotate Key
                </button>
              )}
              <button
                type="button"
                onClick={handleDisconnect}
                className="ml-auto px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm font-medium"
                data-testid="stripe-disconnect-btn"
              >
                Disconnect
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
